import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import { useTranslations } from '@/i18n/use-translations.tsx';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm, useWatch, type Resolver } from 'react-hook-form';
import { useNotifications } from '@/hooks/use-notifications';
import { SESSION_TYPE, type SessionType } from '@/types/dtos/sessions/session-type.enum.ts';
import useUpdateSessionMutation from '@/state/mutations/sessions/use-update-session.ts';
import { updateSessionSchemaFactory } from '@/schemas/sessions/update-session.ts';
import type { SessionDto } from '@/types/dtos/sessions/session.dto.ts';
import TimePicker from '@/components/common/time-picker';
import SessionTypeInfo from '@/components/common/session-type-info';
import { addMinutes, format, parse, startOfDay } from 'date-fns';
import type { UpdateSessionDto } from '@/types/dtos/sessions/request/update-session.dto.ts';
import Widget from '@/components/common/widget.tsx';
import { Timelapse, CategoryOutlined } from '@mui/icons-material';
import { MAX_SESSION_DURATION_MINUTES } from '@/constants';
import { DescriptionFieldWithScrollHint } from '@/components/common/description-field-with-scroll-hint';
import { SessionActions, SessionPublish } from './session-actions';
import useEventMode from '@/components/events/hooks/use-event-mode';
import type { Mode } from '@/types/entity-mode';
import { isOrganizeMode } from '@/components/events/helpers/mode.helper';
import sessionTypeToIcon from '@/components/events/detail/tabs/sessions/helpers/session-type-to-icon.helper';
import { FieldPublishedBaseline } from '@/components/common/field-published-baseline';
import { changedFieldInputSx } from '@/components/common/changed-field-input.sx';

const formatPlannedDuration = (minutes?: number) => (minutes ? format(new Date(0, 0, 0, 0, minutes), 'HH:mm') : '');

export type UpdateSessionFormProps = {
  session: SessionDto;
  /** When set, show plan vs organize comparison inline */
  sessionPlan?: SessionDto | null;
  sessionOrganize?: SessionDto | null;
  /** Show Publish icon in widget header */
  showPublishInHeader?: boolean;
  onShowPublishAlert?: () => void;
  onPublishSuccess?: () => void;
  alternativeWidgetButton?: ReactNode;
  modeOverride?: Mode;
};

const UpdateSessionForm = ({
  session,
  sessionPlan,
  sessionOrganize,
  showPublishInHeader = false,
  onShowPublishAlert,
  onPublishSuccess,
  alternativeWidgetButton,
  modeOverride,
}: UpdateSessionFormProps) => {
  const t = useTranslations();
  const notification = useNotifications();
  const { mutateAsync, isPending } = useUpdateSessionMutation();
  const eventMode = useEventMode();

  const mode = modeOverride ?? eventMode;
  /** Flow detail / wide layout: keep description compact (4 lines); matches narrow. */
  const descriptionRows = 4;

  const showComparison = !!sessionPlan;
  const sessionForEdit = showComparison ? sessionPlan! : session;

  const [lastSavedPlan, setLastSavedPlan] = useState<{
    title: string;
    description: string;
    type: SessionType;
    plannedDurationMinutes: string;
  } | null>(null);

  const updateSessionSchema = updateSessionSchemaFactory(t);
  type SessionFormType = z.infer<typeof updateSessionSchema>;
  const {
    control,
    handleSubmit,
    reset: resetForm,
    formState: { errors, isDirty },
  } = useForm<SessionFormType>({
    resolver: zodResolver(updateSessionSchema) as Resolver<SessionFormType>,
    defaultValues: {
      type: '' as SessionType,
      title: '',
      description: '',
      plannedDurationMinutes: '',
    },
  });
  const lastResetKeyRef = useRef<string | null>(null);
  const lastSessionIdRef = useRef<string | null>(null);

  const watchedValues = useWatch({ control });

  const formValues = useMemo(() => {
    if (!sessionForEdit || !sessionForEdit.type) return null;
    return {
      type: sessionForEdit.type,
      title: sessionForEdit.title,
      description: sessionForEdit.description ?? '',
      plannedDurationMinutes: formatPlannedDuration(sessionForEdit.plannedDurationMinutes),
    };
  }, [sessionForEdit]);

  const fieldDiffs = useMemo(() => {
    if (!showComparison || !sessionOrganize) {
      return { title: false, description: false, type: false, plannedDurationMinutes: false };
    }
    const draftTitle = (watchedValues?.title ?? lastSavedPlan?.title ?? sessionPlan?.title ?? '').trim();
    const draftDesc = (
      watchedValues?.description ??
      lastSavedPlan?.description ??
      sessionPlan?.description ??
      ''
    ).trim();
    const draftType = (watchedValues?.type ?? lastSavedPlan?.type ?? sessionPlan?.type ?? '') as string;
    const draftDuration =
      watchedValues?.plannedDurationMinutes ??
      lastSavedPlan?.plannedDurationMinutes ??
      formatPlannedDuration(sessionPlan?.plannedDurationMinutes);

    const orgTitle = (sessionOrganize.title ?? '').trim();
    const orgDesc = (sessionOrganize.description ?? '').trim();
    const orgType = sessionOrganize.type ?? '';
    const orgDuration = formatPlannedDuration(sessionOrganize.plannedDurationMinutes);

    return {
      title: draftTitle !== orgTitle,
      description: draftDesc !== orgDesc,
      type: draftType !== orgType,
      plannedDurationMinutes: draftDuration !== orgDuration,
    };
  }, [showComparison, sessionOrganize, sessionPlan, lastSavedPlan, watchedValues]);

  const hasPlanOrganizeDiff = useMemo(
    () => fieldDiffs.title || fieldDiffs.description || fieldDiffs.type || fieldDiffs.plannedDurationMinutes,
    [fieldDiffs],
  );

  useEffect(() => {
    if (!sessionPlan) return;
    setLastSavedPlan(null);
  }, [sessionPlan?.id]);

  useEffect(() => {
    if (!formValues || !sessionForEdit) return;
    const resetKey = JSON.stringify({
      ...formValues,
      sessionId: sessionForEdit.id,
      sessionVersion: sessionForEdit.version,
    });
    const sessionChanged = lastSessionIdRef.current && lastSessionIdRef.current !== sessionForEdit.id;
    if (!sessionChanged && (lastResetKeyRef.current === resetKey || isDirty)) return;
    lastResetKeyRef.current = resetKey;
    lastSessionIdRef.current = sessionForEdit.id;
    resetForm(formValues);
  }, [formValues, sessionForEdit?.id, sessionForEdit?.version, resetForm, isDirty]);

  const maxTime = addMinutes(startOfDay(new Date()), MAX_SESSION_DURATION_MINUTES);

  const handleCancel = useCallback(() => {
    if (!formValues) return;
    resetForm(formValues);
  }, [formValues, resetForm]);

  const onSubmit = async (data: SessionFormType) => {
    const updateSessionDto: UpdateSessionDto = {
      id: sessionForEdit!.id,
      version: sessionForEdit!.version,
      title: data.title,
      type: data.type as SessionType,
      description: data.description,
    };
    if (data.plannedDurationMinutes) {
      const plannedDurationMinutes = parse(data.plannedDurationMinutes, 'HH:mm', new Date());
      updateSessionDto.plannedDurationMinutes =
        plannedDurationMinutes.getHours() * 60 + plannedDurationMinutes.getMinutes();
    }

    try {
      const result = await mutateAsync(updateSessionDto);
      notification.notifySuccess(t('session.notification.updateSuccess', { title: result.title }));
      setLastSavedPlan({
        title: data.title,
        description: data.description ?? '',
        type: data.type as SessionType,
        plannedDurationMinutes: data.plannedDurationMinutes ?? '',
      });
      resetForm(data);
    } catch {
      notification.notifyError(t('session.notification.updateError'));
    }
  };

  const publishIcon =
    showPublishInHeader && sessionPlan ? (
      <SessionPublish
        session={sessionPlan}
        indicativeOnly={mode !== 'PLAN'}
        onIndicativeClick={onShowPublishAlert}
        onPublishSuccess={onPublishSuccess}
      />
    ) : null;

  const iconButtons = (
    <Stack direction="row" spacing={0.5} alignItems="center">
      {publishIcon}
      {alternativeWidgetButton ?? <SessionActions session={session} />}
    </Stack>
  );

  const fieldsDisabled = showComparison ? false : isOrganizeMode(mode);
  const showBaselines = showComparison && !!sessionOrganize && hasPlanOrganizeDiff;

  const publishedTitle = sessionOrganize?.title ?? '';
  const publishedDescription = sessionOrganize?.description ?? '';
  const publishedTypeLabel = sessionOrganize?.type ? t(`session.types.${sessionOrganize.type}`) : '';
  const publishedDuration = formatPlannedDuration(sessionOrganize?.plannedDurationMinutes);

  // useWatch can briefly return undefined; mirror fieldDiffs fallbacks so baselines
  // don't flash an empty draft while inputs still show plan defaults.
  const draftTitle = (watchedValues?.title ?? lastSavedPlan?.title ?? sessionPlan?.title ?? '').trim();
  const draftDescription = (
    watchedValues?.description ??
    lastSavedPlan?.description ??
    sessionPlan?.description ??
    ''
  ).trim();
  const draftType = (watchedValues?.type ?? lastSavedPlan?.type ?? sessionPlan?.type ?? '') as SessionType | '';
  const draftTypeLabel = draftType ? t(`session.types.${draftType}`) : '';
  const draftDuration =
    watchedValues?.plannedDurationMinutes ??
    lastSavedPlan?.plannedDurationMinutes ??
    formatPlannedDuration(sessionPlan?.plannedDurationMinutes);

  return (
    <Widget
      title={t('common.basics')}
      iconButtons={isDirty ? undefined : iconButtons}
      primaryButton={
        isDirty ? (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Button
              variant="outlined"
              size="small"
              color="secondary"
              onClick={handleCancel}
              data-testid="cancel-updated-session-button"
            >
              {t('common.cancel')}
            </Button>
            <Button
              data-testid="save-updated-session-button"
              variant="contained"
              onClick={handleSubmit(onSubmit)}
              startIcon={isPending ? <CircularProgress size={20} /> : undefined}
              disabled={showComparison ? false : isOrganizeMode(mode)}
              size="small"
              color="secondary"
            >
              {t('common.save')}
            </Button>
          </Stack>
        ) : undefined
      }
    >
      <Stack component="form" onSubmit={handleSubmit(onSubmit)} gap={0.25} sx={{ flex: 1 }}>
        <Stack spacing={1}>
          <Stack direction="column" spacing={1} alignItems="stretch" sx={{ width: '100%' }}>
            <Box sx={{ width: '100%' }}>
              <Controller
                name="title"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    maxRows={4}
                    multiline
                    variant="standard"
                    size="small"
                    label={t('session.form.titleInputLabel')}
                    placeholder={t('session.form.titleInputPlaceholder')}
                    error={!!errors.title}
                    helperText={errors.title?.message}
                    disabled={fieldsDisabled}
                    sx={showBaselines && fieldDiffs.title ? changedFieldInputSx : undefined}
                  />
                )}
              />
              {showBaselines && fieldDiffs.title && (
                <FieldPublishedBaseline publishedValue={publishedTitle} draftValue={draftTitle} />
              )}
            </Box>
            <Box sx={{ width: '100%' }}>
              <Controller
                name="type"
                control={control}
                render={({ field }) => (
                  <FormControl
                    variant="standard"
                    size="small"
                    error={!!errors.type}
                    fullWidth
                    sx={showBaselines && fieldDiffs.type ? changedFieldInputSx : undefined}
                  >
                    <InputLabel id="update-session-type-label">{t('session.form.typeInputLabel')}</InputLabel>
                    <Select
                      {...field}
                      labelId="update-session-type-label"
                      label={t('session.form.typeInputLabel')}
                      size="small"
                      disabled={fieldsDisabled}
                      displayEmpty
                      renderValue={(value) => {
                        const selected = value as SessionType | '';
                        if (!selected) {
                          return (
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'text.secondary' }}>
                              <CategoryOutlined sx={{ fontSize: 22, opacity: 0.7 }} />
                              <Typography variant="body2">{t('session.form.selectType')}</Typography>
                            </Stack>
                          );
                        }
                        return (
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, width: '100%' }}>
                            <Box
                              sx={{
                                'display': 'flex',
                                'alignItems': 'center',
                                'fontSize': 22,
                                'lineHeight': 1,
                                'flexShrink': 0,
                                '& svg': { fontSize: 'inherit' },
                              }}
                            >
                              {sessionTypeToIcon(selected)}
                            </Box>
                            <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
                              {t(`session.types.${selected}`)}
                            </Typography>
                          </Stack>
                        );
                      }}
                      sx={{
                        'width': '100%',
                        '& .MuiSelect-select': {
                          display: 'flex',
                          alignItems: 'center',
                          py: 0.5,
                          pr: '28px !important',
                        },
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            'maxWidth': 360,
                            '& .MuiMenuItem-root': {
                              fontSize: '0.875rem',
                              lineHeight: '1.25rem',
                              padding: '6px 8px',
                              minHeight: '32px',
                            },
                          },
                        },
                      }}
                    >
                      <MenuItem value="" disabled>
                        {t('session.form.selectType')}
                      </MenuItem>
                      {Object.values(SESSION_TYPE).map((type) => (
                        <MenuItem key={type} value={type}>
                          <SessionTypeInfo type={type} />
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.type && (
                      <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.25 }}>
                        {errors.type.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />
              {showBaselines && fieldDiffs.type && (
                <FieldPublishedBaseline publishedValue={publishedTypeLabel} draftValue={draftTypeLabel} />
              )}
            </Box>
          </Stack>
          {session.isInLibrary && (
            <Box sx={{ width: '100%' }}>
              <Controller
                name="plannedDurationMinutes"
                control={control}
                render={({ field }) => (
                  <TimePicker
                    label={t('session.form.plannedDurationInputLabel')}
                    ampm={false}
                    durationMode={true}
                    icon={Timelapse}
                    maxTime={maxTime}
                    views={['hours', 'minutes']}
                    value={field.value ? parse(field.value, 'HH:mm', new Date()) : null}
                    onChange={(value) => {
                      if (value) {
                        field.onChange(format(value, 'HH:mm'));
                      } else {
                        field.onChange(null);
                      }
                    }}
                    disabled={fieldsDisabled}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        variant: 'standard',
                        size: 'small',
                        error: !!errors.plannedDurationMinutes,
                        helperText: errors.plannedDurationMinutes?.message,
                        sx: showBaselines && fieldDiffs.plannedDurationMinutes ? changedFieldInputSx : undefined,
                      },
                    }}
                  />
                )}
              />
              {showBaselines && fieldDiffs.plannedDurationMinutes && (
                <FieldPublishedBaseline publishedValue={publishedDuration} draftValue={draftDuration} />
              )}
            </Box>
          )}
        </Stack>
        <Box>
          <Controller
            name="description"
            control={control}
            render={({ field }) => {
              const { ref, ...fieldProps } = field;
              return (
                <DescriptionFieldWithScrollHint
                  ref={ref}
                  {...fieldProps}
                  descriptionRows={descriptionRows}
                  disabled={fieldsDisabled}
                  error={!!errors.description}
                  helperText={errors.description?.message}
                  label={t('session.form.descriptionInputLabel')}
                  scrollForMoreLabel={t('flows.form.scrollForMore', { defaultValue: 'Scroll for more' })}
                  size="small"
                  sx={showBaselines && fieldDiffs.description ? changedFieldInputSx : undefined}
                />
              );
            }}
          />
          {showBaselines && fieldDiffs.description && (
            <FieldPublishedBaseline publishedValue={publishedDescription} draftValue={draftDescription} />
          )}
        </Box>
      </Stack>
    </Widget>
  );
};

export default UpdateSessionForm;
